<template>
  <div v-if="isOpen" class="modal-overlay">
    <div class="modal">
      <h2>{{ $t('settings') }}</h2>

      <!-- Afficher les boutons uniquement si la base n'est pas en cours de mise à jour -->
      <div class="button-group">
        <button :class="{ active: selectedModule === 'volume' }" @click="selectedModule = 'volume'">
          {{ $t('editVolume') }}
        </button>
        <button :class="{ active: selectedModule === 'ratio' }" @click="selectedModule = 'ratio'">
          {{ $t('editRatio') }}
        </button>
        <button :class="{ active: selectedModule === 'thresholdic' }" @click="selectedModule = 'thresholdic'">
          {{ $t('editThresholdIc') }}
        </button>
        <button :class="{ active: selectedModule === 'thresholdprodms' }" @click="selectedModule = 'thresholdprodms'">
          {{ $t('editThresholdProdMs') }}
        </button>
        <button :class="{ active: selectedModule === 'thresholdprodmi' }" @click="selectedModule = 'thresholdprodmi'">
          {{ $t('editThresholdProdMi') }}
        </button>
        <button :class="{ active: selectedModule === 'reset' }" class="reset-tab-btn" @click="selectedModule = 'reset'">
          {{ $t('resetDb') }}
        </button>
      </div>

      <!-- Volume -->
      <div v-if="selectedModule === 'volume' && !isUpdatingDB" class="module">
        <div>
          <h1>{{ $t('editVolume') }}</h1>
          <div class="table-container">
            <table class="styled-table">
              <thead>
                <tr>
                  <th>{{ $t('pbrLabel') }}</th>
                  <th>{{ $t('actualVolume') }}</th>
                  <th>{{ $t('newVolume') }}</th>
                  <th>{{ $t('update') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pbr in pbrFiltered" :key="pbr.id">
                  <td>
                    <select v-model="dataStore.filtrePBR_setting" class="styled-dropdown">
                      <option v-for="pbr in dataStore.pbrs" :value="pbr.id" :key="pbr.id">
                        {{ pbr.numero_pbr }}
                      </option>
                    </select>
                  </td>
                  <td>{{ pbr.volume ?? 'Non défini' }}</td>
                  <td>
                    <input v-model.number="pbr.newVolume" type="number" step="0.1" />
                  </td>
                  <td>
                    <button class="custom-btn"
                      :disabled="pbr.newVolume === undefined || pbr.newVolume === null || pbr.newVolume < 0"
                      @click="updateVolume(pbr)">
                      ✔️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Ratio -->
      <div v-if="selectedModule === 'ratio' && !isUpdatingDB" class="module">
        <div>
          <h1>{{ $t('editRatio') }}</h1>
          <div class="table-container">
            <table class="styled-table">
              <thead>
                <tr>
                  <th>{{ $t('speciesLabel') }}</th>
                  <th>{{ $t('actualRatio') }}</th>
                  <th>{{ $t('newRatio') }}</th>
                  <th>{{ $t('update') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="specie in specieFiltered" :key="specie.id">
                  <td>
                    <select v-model="dataStore.filtreEspece_setting" class="styled-dropdown">
                      <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
                        {{ specie.specie_code }}
                      </option>
                    </select>
                  </td>
                  <td>{{ specie.ratio ?? 'Non défini' }}</td>
                  <td>
                    <input v-model.number="specie.newRatio" type="number" step="0.001" />
                  </td>
                  <td>
                    <button class="custom-btn"
                      :disabled="specie.newRatio === undefined || specie.newRatio === null || specie.newRatio < 0"
                      @click="updateRatio(specie)">
                      ✔️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Thresholds (IC) -->
      <div v-if="selectedModule === 'thresholdic' && !isUpdatingDB" class="module">
        <div>
          <h1>{{ $t('editThresholdIc') }}</h1>
          <div class="table-container">
            <table class="styled-table">
              <thead>
                <tr>
                  <th>{{ $t('speciesLabel') }}</th>
                  <th>{{ $t('threshold1') }}</th>
                  <th>{{ $t('threshold3') }}</th>
                  <th>{{ $t('threshold3') }}</th>
                  <th>{{ $t('update') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="specie in specieFiltered" :key="specie.id">
                  <td>
                    <select v-model="dataStore.filtreEspece_setting" class="styled-dropdown">
                      <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
                        {{ specie.specie_code }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[0]" type="number" step="0.1" min="0"
                      :max="dataStore.localThresholds[0] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[1]" type="number" step="0.1"
                      :min="dataStore.localThresholds[0] ?? 0" :max="dataStore.localThresholds[1] ?? 0"
                      placeholder="Non défini" />
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[2]" type="number" step="0.1"
                      :min="dataStore.localThresholds[1] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <button class="custom-btn"
                      :disabled="dataStore.localThresholds.every(val => val === null || val === undefined)"
                      @click="handleUpdateThreshold(dataStore.filtreEspece_setting)">
                      ✔️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Thresholds (ProdMs) -->
      <div v-if="selectedModule === 'thresholdprodms' && !isUpdatingDB" class="module">
        <div>
          <h1>{{ $t('editThresholdProdMs') }}</h1>
          <div class="table-container">
            <table class="styled-table">
              <thead>
                <tr>
                  <th>{{ $t('speciesLabel') }}</th>
                  <th>{{ $t('threshold1') }}</th>
                  <th>{{ $t('threshold2') }}</th>
                  <th>{{ $t('update') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="specie in specieFiltered" :key="specie.id">
                  <td>
                    <select v-model="dataStore.filtreEspece_setting" class="styled-dropdown">
                      <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
                        {{ specie.specie_code }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[3]" type="number" step="0.1" min="0"
                      :max="dataStore.localThresholds[4] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[4]" type="number" step="0.1"
                      :min="dataStore.localThresholds[3] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <button class="custom-btn"
                      :disabled="dataStore.localThresholds.every(val => val === null || val === undefined)"
                      @click="handleUpdateThreshold(dataStore.filtreEspece_setting)">
                      ✔️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Thresholds (ProdMs) -->
      <div v-if="selectedModule === 'thresholdprodmi' && !isUpdatingDB" class="module">
        <div>
          <h1>{{ $t('editThresholdProdMi') }}</h1>
          <div class="table-container">
            <table class="styled-table">
              <thead>
                <tr>
                  <th>{{ $t('speciesLabel') }}</th>
                  <th>{{ $t('threshold1') }}</th>
                  <th>{{ $t('threshold2') }}</th>
                  <th>{{ $t('update') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="specie in specieFiltered" :key="specie.id">
                  <td>
                    <select v-model="dataStore.filtreEspece_setting" class="styled-dropdown">
                      <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
                        {{ specie.specie_code }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[5]" type="number" step="0.1" min="0"
                      :max="dataStore.localThresholds[6] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <input v-model.number="dataStore.localThresholds[6]" type="number" step="0.1"
                      :min="dataStore.localThresholds[5] ?? 0" placeholder="Non défini" />
                  </td>
                  <td>
                    <button class="custom-btn"
                      :disabled="dataStore.localThresholds.every(val => val === null || val === undefined)"
                      @click="handleUpdateThreshold(dataStore.filtreEspece_setting)">
                      ✔️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Reset DB -->
      <div v-if="selectedModule === 'reset'" class="module">
        <div class="reset-section">
          <h1>{{ $t('resetDb') }}</h1>
          <p class="reset-warning">{{ $t('resetDbConfirm') }}</p>
          <div v-if="isUpdatingDB" class="reset-loading">
            <div class="spinner"></div>
            <p>{{ $t('resetDbLoading') }}</p>
          </div>
          <div v-else-if="resetDone" class="reset-done">
            <p>{{ $t('resetDbSuccess') }}</p>
            <button class="custom-btn" @click="() => window.location.reload()">{{ $t('reloadPage') }}</button>
          </div>
          <div v-else>
            <button class="custom-btn danger-btn" @click="handleResetDb">
              {{ $t('resetDbButton') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="!isUpdatingDB" class="button-group">
        <button @click="closeModal">{{ $t('close') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { updateThreshold } from '@/functions/functions_pilotage_api';
import backendApi from '@/axios/backendApi';
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast()
const dataStore = useDataStore();
const error = ref<string | null>(null);
const isUpdatingDB = ref(false);
const resetDone = ref(false);
const selectedModule = ref('volume');
const window = globalThis;

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits(['close']);

const closeModal = () => {
  emit('close');
  dataStore.filtrePBR_setting = 1
  dataStore.filtreEspece_setting = 2
  selectedModule.value = 'volume'
};

onMounted(async () => {
  dataStore.pbrs = dataStore.pbrs.map(pbr => ({
    ...pbr,
    newVolume: undefined
  }));
  dataStore.species = dataStore.species.map(specie => ({
    ...specie,
    newRatio: undefined
  }));
  dataStore.filtrePBR_setting = 1;
  dataStore.filtreEspece_setting = 2;
});

const pbrFiltered = computed(() => {
  if (dataStore.filtrePBR_setting) {
    return dataStore.pbrs.filter(
      (p: PBR) => p.id === dataStore.filtrePBR_setting
    );
  }
  return dataStore.pbrs;
});

const specieFiltered = computed(() => {
  if (dataStore.filtreEspece_setting) {
    return dataStore.species.filter(
      (s: Species) => s.id === dataStore.filtreEspece_setting
    );
  }
  return dataStore.species;
});

watch(
  () => dataStore.filtreEspece_setting,
  (newSpecieId) => {
    if (!dataStore.isBasicDataLoaded) {
      return;
    }
    if (newSpecieId && dataStore.thresholds[newSpecieId]) {
      dataStore.localThresholds = [...dataStore.thresholds[newSpecieId]];
    } else {
      dataStore.localThresholds = Array(7).fill(null);
    }
  },
  { immediate: true }
);

const handleUpdateThreshold = async (specieID: number | null) => {
  console.log(specieID);
  if (specieID === null || specieID === 0) return;
  
  const originalValues = dataStore.thresholds[specieID] ?? Array(7).fill(null);
  const newValues = [...dataStore.localThresholds];

  // Valider les seuils (valeurs croissantes)
  // Pour IC (indices 0,1,2)
  if (newValues[0] !== null && newValues[1] !== null && newValues[0] > newValues[1]) {
    newValues[1] = newValues[0];
    dataStore.localThresholds[1] = newValues[0];
  }
  if (newValues[1] !== null && newValues[2] !== null && newValues[1] > newValues[2]) {
    newValues[2] = newValues[1];
    dataStore.localThresholds[2] = newValues[1];
  }

  // Pour ProdMS (indices 3,4)
  if (newValues[3] !== null && newValues[4] !== null && newValues[3] > newValues[4]) {
    newValues[4] = newValues[3];
    dataStore.localThresholds[4] = newValues[3];
  }

  // Pour ProdMi (indices 5,6)
  if (newValues[5] !== null && newValues[6] !== null && newValues[5] > newValues[6]) {
    newValues[6] = newValues[5];
    dataStore.localThresholds[6] = newValues[5];
  }

  try {
    await updateThreshold(specieID, newValues);
    dataStore.setThreshold(specieID, newValues);
    toast.success(t('thresholdUpdateSuccess'));
  } catch (error) {
    console.error(`Erreur lors de la mise à jour des seuils:`, error);
    dataStore.localThresholds = [...originalValues];
    toast.error(t('thresholdUpdateError'));
  }
};

const updateVolume = async (pbr: PBR) => {
  try {
    await backendApi.put(`/pbrs/${pbr.id}`, {
      numero_pbr: pbr.numero_pbr,
      volume: pbr.newVolume
    });
    pbr.volume = pbr.newVolume;
    pbr.newVolume = undefined;
    toast.success(t('pbrUpdateSuccess'));
  } catch (err) {
    error.value = t('pbrUpdateError');
    toast.error(error.value)
  }
};

const handleResetDb = async () => {
  isUpdatingDB.value = true;
  resetDone.value = false;
  try {
    await backendApi.post('/admin/reset-db', {}, { timeout: 300000 });
    resetDone.value = true;
    toast.success(t('resetDbSuccess'));
  } catch (err) {
    toast.error(t('resetDbError'));
  } finally {
    isUpdatingDB.value = false;
  }
};

const updateRatio = async (specie: Species) => {
  try {
    await backendApi.put(`/species/${specie.id}`, {
      specie_code: specie.specie_code,
      specie_name: specie.specie_name,
      ratio: specie.newRatio
    });
    specie.ratio = specie.newRatio;
    specie.newRatio = null;
    toast.success(t('specieUpdateSuccess'));
  } catch (err) {
    error.value = t('specieUpdateError');
    toast.error(error.value)
  }
};
</script>

<style scoped>
.reset-tab-btn {
  background-color: #c0392b !important;
  color: white !important;
}

.reset-section {
  padding: 20px 0;
}

.reset-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 16px 0;
  color: #856404;
}

.reset-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #ccc;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.reset-done {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
}

.danger-btn {
  background-color: #c0392b;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
}

.danger-btn:hover {
  background-color: #a93226;
}

.styled-dropdown {
  margin-bottom: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-height: 80%;
  width: 800px;
}

.modal-content select,
.modal-content input {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
</style>